import { useCallback, useState } from 'react'
import { Permissions } from '../../../../types/user-types'
import SummaryTable from '../../wells/summary-table'
import { ERROR_MESSAGES } from '../../errors/messages'
import { useWellId } from '../../../hooks/use-well-id'
import { ColumnHeader, InlineNotification, TableAction } from '@msaf/core-react'

interface ExternalDocumentReferenceSummaryTableProps {
  fields: Record<string, string | number | boolean>[] | null
  setFields: React.Dispatch<React.SetStateAction<Record<string, string | number | boolean>[] | null>>
}

export default function ExternalDocumentReferenceTable({
  fields,
  setFields,
}: ExternalDocumentReferenceSummaryTableProps) {
  const wellId = useWellId()
  const [rowId, setRowId] = useState<number | null>(null)

  const onModalClose = () => {
    setRowId(null)
  }

  const setFieldsWithTransform: typeof setFields = useCallback(
    (value) => {
      if (typeof value === 'function') {
        setFields((prev) => {
          const next = value(prev)
          return next?.map((row) => ({ ...row, documentLinkIsPublic: row.documentLinkIsPublic ? 'Yes' : 'No' })) ?? next
        })
      } else {
        setFields(
          value?.map((row) => ({ ...row, documentLinkIsPublic: row.documentLinkIsPublic ? 'Yes' : 'No' })) ?? value,
        )
      }
    },
    [setFields],
  )

  const rowClickAction: TableAction = {
    label: 'View',
    type: 'transitionAction',
    args: [{ constant: `wells/${wellId}/view/external-document-references` }, { elementKey: 'id' }],
  }

  const actions: Array<TableAction> = [
    {
      label: 'Delete',
      type: 'deleteAction',
      args: [
        {
          elementKey: 'id',
        },
      ],
      actionPermissions: [Permissions.EDIT_EXTERNAL_DOCUMENTS],
    },
  ]

  const columnHeaders: Array<ColumnHeader> = [
    {
      elementKey: 'id',
      columnHeading: 'Id',
      viewColumn: 'id',
      type: 'hidden',
    },
    {
      elementKey: 'dateAdded',
      columnHeading: 'Date added',
      viewColumn: 'dateAdded',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'documentDescription',
      columnHeading: 'Document description',
      viewColumn: 'documentDescription',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'documentType',
      columnHeading: 'Document type',
      viewColumn: 'documentType',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'documentReference',
      columnHeading: 'Document reference',
      viewColumn: 'documentReference',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'documentLinkIsPublic',
      columnHeading: 'Public link?',
      viewColumn: 'documentLinkIsPublic',
      sortable: true,
      type: 'text',
    },
    {
      elementKey: 'actions',
      columnHeading: 'Actions',
      viewColumn: 'actions',
      type: 'actions-no-data',
      actions,
    },
  ]

  const deleteAction = (id: number) => {
    setRowId(id)
  }

  return (
    <SummaryTable
      fields={fields}
      setFields={setFieldsWithTransform}
      columnHeaders={columnHeaders}
      defaultSortBy={{
        orderColumn: 'dateAdded',
      }}
      rowClickAction={rowClickAction}
      requestUrl={`wells/${wellId}/external-document-references`}
      tableActionHandlers={{
        deleteAction,
      }}
      confirmDeleteModalProps={{
        isShowModal: !!rowId,
        requestUrl: `wells/${wellId}/external-document-references/${rowId}/edit`,
        heading: 'Delete external document reference',
        bodyText: 'This will delete the external document reference. Are you sure you wish to proceed?',
        successMessage: 'External document reference deleted',
        errorMessage: ERROR_MESSAGES.DELETE_FAILED,
        onModalClose,
      }}
      noResultsComponent={
        <InlineNotification messageType='info' isDismissable={false}>
          No document references added
        </InlineNotification>
      }
      resultNameSingular='document'
      resultNamePlural='documents'
    />
  )
}
